import type { ChartData } from '@/types/chart';

function normalizeText(value: unknown): string {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

function normalizeWhitespace(value: unknown): string {
  return String(value || '')
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function isGenericAxisLabel(value: unknown): boolean {
  const text = normalizeText(value);
  return !text || text === 'x' || text === 'y' || text === 'categoria' || text === 'valor';
}

function cleanAxisLabel(value: unknown, fallback: string): string {
  const cleaned = normalizeWhitespace(value)
    .replace(/\s*-\s*(?:flota completa|toda la flota).*$/i, '')
    .replace(/^dispersi[oó]n\s+(?:de|del|la|el)?\s*/i, '')
    .replace(/^gr[aá]fica\s+(?:de|del|la|el)?\s*/i, '')
    .replace(/\s+/g, ' ')
    .trim();

  if (!cleaned) {
    return fallback;
  }

  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
}

function canonicalAxisLabel(value: string): string {
  const text = normalizeText(value);
  if (!text) return '';
  if (text.includes('distancia') || text.includes('recorrido') || text.includes('kilomet')) {
    return 'Distancia restante (km)';
  }
  if (text.includes('riesgo') || text.includes('porcentaje') || text.includes('theft') || text.includes('risk')) {
    return 'Porcentaje de riesgo (%)';
  }
  if (text.includes('velocidad') || text.includes('km/h') || text.includes('speed')) {
    return 'Velocidad promedio (km/h)';
  }
  if (text.includes('eta') || text.includes('minuto') || /\bmin\b/.test(text)) {
    return 'ETA restante (min)';
  }
  return cleanAxisLabel(value, '');
}

function parseScatterAxisLabels(chart: ChartData): { x?: string; y?: string } {
  const sources = [
    chart.x_label,
    chart.y_label,
    chart.original_instruction,
    chart.title,
    chart.dataset_label,
  ].filter(Boolean) as string[];

  for (const source of sources) {
    const display = normalizeWhitespace(source);
    const vsMatch = display.match(/(?:dispersi[oó]n(?:\s+operativa)?(?:\s+de)?\s+)?(.+?)\s+vs\.?\s+(.+)/i);
    if (vsMatch) {
      return {
        x: canonicalAxisLabel(vsMatch[1]),
        y: canonicalAxisLabel(vsMatch[2]),
      };
    }

    const contraMatch = display.match(/(?:dispersi[oó]n(?:\s+operativa)?(?:\s+de)?\s+)?(.+?)\s+contra\s+(?:la|el|los|las)?\s*(.+)/i);
    if (contraMatch) {
      return {
        x: canonicalAxisLabel(contraMatch[2]),
        y: canonicalAxisLabel(contraMatch[1]),
      };
    }
  }

  return {};
}

export interface ChartPresentation {
  displayTitle: string;
  datasetLabel: string;
  categoryHeader: string;
  valueHeader: string;
  scatterXHeader: string;
  scatterYHeader: string;
}

export function resolveChartPresentation(chart: ChartData, activeFilter?: string): ChartPresentation {
  const displayTitle = activeFilter && chart.title
    ? chart.title.replace(/- .+$/, `- ${activeFilter}`)
    : chart.title;
  const parsedScatterLabels = parseScatterAxisLabels(chart);
  const datasetLabel = cleanAxisLabel(chart.dataset_label || chart.y_label || 'Valor', 'Valor');
  const categoryHeader = cleanAxisLabel(chart.x_label || 'Categoría', 'Categoría');
  const valueHeader = cleanAxisLabel(chart.y_label || chart.dataset_label || 'Valor', 'Valor');
  const scatterXHeader = isGenericAxisLabel(chart.x_label)
    ? cleanAxisLabel(parsedScatterLabels.x || 'X', 'X')
    : cleanAxisLabel(chart.x_label, 'X');
  const scatterYHeader = isGenericAxisLabel(chart.y_label)
    ? cleanAxisLabel(parsedScatterLabels.y || chart.dataset_label || 'Y', 'Y')
    : cleanAxisLabel(chart.y_label, 'Y');

  return {
    displayTitle,
    datasetLabel,
    categoryHeader,
    valueHeader,
    scatterXHeader,
    scatterYHeader,
  };
}
